import React, { useRef } from 'react';
import { Camera, Download, FileUp, Loader2, Paperclip, X } from 'lucide-react';
import { STAMP_SIGNER_NAME_MAX_LENGTH } from '../../lib/approval-stamp';
import { fmtFileSize } from '../../lib/display-labels';

/**
 * Approval by uploading the signed ticket, on the public review page (api#721).
 *
 * THE SERVER DECIDES WHAT IS ACCEPTABLE, AND THIS COMPONENT DOES NOT GUESS
 *
 * The route checks the file's MAGIC BYTES — `%PDF-`, the PNG signature, the
 * JPEG SOI — at offset zero, and ignores both the extension and the `type` the
 * browser reported. Any check here would be a check of something else: `File.type`
 * is the OS's guess from the extension, and the two disagree exactly when it
 * matters (a `.pdf` that is really a Word document; a `.jpeg` that is a HEIC a
 * converter renamed). A local guess that says no where the server would say yes
 * is a refusal the customer cannot appeal and nobody can see; one that says yes
 * where the server says no has simply moved the error later. So nothing is
 * rejected here. The one 20 MB figure below is COPY, not a gate — it tells the
 * consultant the rule before they spend four minutes uploading, and if it ever
 * drifts from the server's the server still wins and still says so.
 *
 * `accept` is deliberately wide: `image/*` rather than the three types the
 * server takes. On iOS a narrow image accept can hide camera-roll items, and
 * `image/*` is what makes Safari transcode a HEIC photo to JPEG on pick — which
 * turns the commonest wellsite file into one the server accepts. Narrowing it to
 * `image/jpeg,image/png` would refuse, in the OS picker, the very photo the
 * consultant just took.
 *
 * THE PHONE IS THE PRIMARY CASE
 *
 * Two doors, because iOS gives one or the other and never both: an input with
 * `capture` opens the camera directly, and an input WITHOUT it opens Photos /
 * Files — the camera roll. A single input cannot offer both, so there are two,
 * and the labels say which is which rather than leaving the consultant to
 * discover that "Choose a file" means "and not the photo you just took".
 */

export interface ApprovalFileUploadProps {
  approverName: string;
  onApproverNameChange: (v: string) => void;
  file: File | null;
  onFileChange: (f: File | null) => void;
  /** The API's refusal, verbatim, or null. */
  error: string | null;
  submitting: boolean;
  onSubmit: () => void;
  /** Label for the name field — the operator's own wording when it has a stamp. */
  nameLabel: string;
  /**
   * The review link's PDF endpoint, or null when there is no token to build one
   * from. The consultant on this path needs the document BEFORE they can stamp
   * it, and the copy in the page header is above the fold on a desktop and off
   * it on a phone — which is where this path is actually used. Absent is not an
   * empty button: the step disappears rather than rendering a dead link.
   */
  pdfUrl?: string | null;
}

/** 20 MB as the API counts it: 20 * 1024 * 1024 (STAMPED_FILE_MAX_BYTES). */
export const STAMPED_FILE_MAX_MB = 20;

const ApprovalFileUpload: React.FC<ApprovalFileUploadProps> = ({
  approverName,
  onApproverNameChange,
  file,
  onFileChange,
  error,
  submitting,
  onSubmit,
  nameLabel,
  pdfUrl,
}) => {
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);

  // Reset `value` after every pick. Without it, choosing the same file twice in
  // a row fires no `change` event, so a consultant who removed a file and
  // reached for the same one again would get a picker that appears to do nothing.
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (chosen) onFileChange(chosen);
  };

  const ready = approverName.trim() !== '' && !!file;

  return (
    <div className="bg-white rounded-xl shadow-sm border p-5 space-y-4" data-testid="approval-file-upload">
      <div>
        <h3 className="font-bold text-gray-900">Upload the signed ticket</h3>
        <p className="text-sm text-gray-600 mt-1">
          Take a photo of the ticket you signed, or attach a PDF. PDF, PNG or JPEG, up to{' '}
          {STAMPED_FILE_MAX_MB} MB.
        </p>
      </div>

      {/* Step one, stated as a step. The three actions are in the order they
          happen, because the commonest failure on this path is a consultant who
          uploads a photo of an UNSIGNED ticket — they never had the document to
          sign. */}
      {pdfUrl && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="upload-path-download">
          <p className="text-sm text-gray-700 mb-2">
            Download the ticket first, sign or stamp it, then upload it back here.
          </p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
          >
            <Download size={16} />
            Download PDF
          </a>
        </div>
      )}

      <div>
        <label htmlFor="upload-approver-name" className="block text-xs text-gray-500 mb-1">
          {nameLabel} <span className="text-red-500">*</span>
        </label>
        <input
          id="upload-approver-name"
          className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
          placeholder="e.g. John Smith"
          value={approverName}
          maxLength={STAMP_SIGNER_NAME_MAX_LENGTH}
          disabled={submitting}
          onChange={e => onApproverNameChange(e.target.value)}
        />
      </div>

      {/* Two inputs, two doors — see the header. Visually suppressed, and
          driven by the buttons below, so the control can be labelled in words. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="approval-camera-input"
        onChange={pick}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        data-testid="approval-library-input"
        onChange={pick}
      />

      {file ? (
        <div className="flex items-center gap-3 border rounded-lg px-3 py-2.5 bg-gray-50">
          <Paperclip size={16} className="text-gray-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-900 truncate" data-testid="approval-file-name">{file.name}</p>
            <p className="text-xs text-gray-500">{fmtFileSize(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            disabled={submitting}
            aria-label="Remove file"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={submitting}
            className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl py-5 text-gray-700 hover:border-amber-400 hover:text-amber-700 transition-colors disabled:opacity-40"
          >
            <Camera size={22} />
            <span className="text-sm font-medium">Take a photo</span>
          </button>
          <button
            type="button"
            onClick={() => libraryRef.current?.click()}
            disabled={submitting}
            className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 rounded-xl py-5 text-gray-700 hover:border-amber-400 hover:text-amber-700 transition-colors disabled:opacity-40"
          >
            <FileUp size={22} />
            <span className="text-sm font-medium">Choose a file</span>
          </button>
        </div>
      )}

      {/* The API's own words. Rate limits, wrong file type and oversize all
          arrive here as written — this page has no opinion about any of them,
          and a friendlier paraphrase would be a second, drifting copy of rules
          that live in one place. */}
      {error && (
        <p className="text-sm text-red-600" role="alert" data-testid="approval-upload-error">
          {error}
        </p>
      )}

      <button
        onClick={onSubmit}
        disabled={!ready || submitting}
        className="w-full bg-green-600 text-white py-3.5 rounded-xl text-base font-semibold hover:bg-green-700 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={18} className="animate-spin" /> : <FileUp size={18} />}
        {submitting ? 'Uploading...' : 'Upload & Approve'}
      </button>
    </div>
  );
};

export default ApprovalFileUpload;
